var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import client from 'api/environment';
import ADD_MODEL_SPECIFICATION_STATUS from 'api/mutations/ADD_MODEL_SPECIFICATION_STATUS';
import REMOVE_MODEL_SPECIFICATION_STATUS from 'api/mutations/REMOVE_MODEL_SPECIFICATION_STATUS';
import UPDATE_MODEL_SPECIFICATION_STATUS from 'api/mutations/UPDATE_MODEL_SPECIFICATION_STATUS';
import GET_CURRENT_TENANT_STATUSES from 'api/queries/GET_CURRENT_TENANT_STATUSES';
export const initialState = {
    items: [],
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    status: {
        fetch: 'idle',
        add: 'idle',
        update: 'idle',
        remove: 'idle',
    },
    errors: {
        fetch: null,
        add: null,
        update: null,
        remove: null,
    },
    refetchFlags: {
        fetch: false,
        add: false,
        update: false,
        remove: false,
    },
};
export const fetchStatuses = createAsyncThunk('statuses/fetchStatuses', (_, { getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    const state = getState();
    const { pageInfo } = state.parameters.statuses;
    try {
        const response = yield client.query({
            query: GET_CURRENT_TENANT_STATUSES,
            variables: {
                first: 50,
                after: pageInfo.endCursor,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.me.currentTenant.statuses;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const addStatus = createAsyncThunk('statuses/addStatus', ({ namePL, nameEN, color }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const statusData = yield client.mutate({
            mutation: ADD_MODEL_SPECIFICATION_STATUS,
            variables: { namePL, nameEN, color },
            fetchPolicy: 'no-cache',
        });
        return statusData.data.addModelSpecificationStatus.status;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const updateStatus = createAsyncThunk('statuses/updateStatus', ({ statusId, namePL, nameEN, color, }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const statusData = yield client.mutate({
            mutation: UPDATE_MODEL_SPECIFICATION_STATUS,
            variables: { statusId, namePL, nameEN, color },
        });
        return statusData.data.updateModelSpecificationStatus.status;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const removeStatus = createAsyncThunk('statuses/removeStatus', (statusId, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield client.mutate({
            mutation: REMOVE_MODEL_SPECIFICATION_STATUS,
            variables: { statusId },
        });
        return statusId;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const statusesSlice = createSlice({
    name: 'statuses',
    initialState,
    reducers: {
        resetStatuses(state) {
            state.items = [];
            state.pageInfo = initialState.pageInfo;
            state.status = initialState.status;
            state.errors = initialState.errors;
            state.refetchFlags = initialState.refetchFlags;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchStatuses.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchStatuses.fulfilled, (state, action) => {
            var _a;
            const newStatuses = action.payload.edges.map((edge) => edge.node);
            const uniqueStatuses = newStatuses.filter((newStatus) => !state.items.some((existingStatus) => existingStatus.id === newStatus.id));
            state.items = [...state.items, ...uniqueStatuses];
            state.pageInfo = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.pageInfo;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchStatuses.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch =
                action.payload || action.error.message || 'Error fetching statuses';
        })
            .addCase(addStatus.pending, (state) => {
            state.status.add = 'loading';
            state.errors.add = null;
        })
            .addCase(addStatus.fulfilled, (state, action) => {
            var _a, _b, _c, _d;
            state.items = [
                ...state.items,
                Object.assign(Object.assign({}, action.payload), { canBeUpdated: (_b = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.canBeUpdated) !== null && _b !== void 0 ? _b : true, canBeRemoved: (_d = (_c = action.payload) === null || _c === void 0 ? void 0 : _c.canBeRemoved) !== null && _d !== void 0 ? _d : true }),
            ];
            state.status.add = 'succeeded';
        })
            .addCase(addStatus.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add =
                action.payload || action.error.message || 'Failed to add status';
        })
            .addCase(updateStatus.pending, (state) => {
            state.status.update = 'loading';
            state.errors.update = null;
        })
            .addCase(updateStatus.fulfilled, (state, action) => {
            const { id, namePL, nameEN, color, canBeUpdated, canBeRemoved } = action.payload;
            const itemIndex = state.items.findIndex((item) => item.id === id);
            if (itemIndex !== -1) {
                state.items[itemIndex] = Object.assign(Object.assign({}, state.items[itemIndex]), { namePL,
                    nameEN,
                    color, canBeUpdated: typeof canBeUpdated === 'boolean'
                        ? canBeUpdated
                        : state.items[itemIndex].canBeUpdated, canBeRemoved: typeof canBeRemoved === 'boolean'
                        ? canBeRemoved
                        : state.items[itemIndex].canBeRemoved });
            }
            state.status.update = 'succeeded';
        })
            .addCase(updateStatus.rejected, (state, action) => {
            state.status.update = 'failed';
            state.errors.update =
                action.payload || action.error.message || 'Failed to update status';
        })
            .addCase(removeStatus.pending, (state) => {
            state.status.remove = 'loading';
            state.errors.remove = null;
        })
            .addCase(removeStatus.fulfilled, (state, action) => {
            state.items = state.items.filter((status) => status.id !== action.payload);
            state.status.remove = 'succeeded';
        })
            .addCase(removeStatus.rejected, (state, action) => {
            state.status.remove = 'failed';
            state.errors.remove =
                action.payload || action.error.message || 'Failed to remove status';
        });
    },
});
export const { resetStatuses } = statusesSlice.actions;
export const selectStatuses = (state) => state.parameters.statuses.items;
export const selectStatusById = (state, statusId) => state.parameters.statuses.items.find((status) => status.id === statusId);
export const selectStatusesPageInfo = (state) => state.parameters.statuses.pageInfo;
export const selectStatusesStatus = (state) => state.parameters.statuses.status;
export const selectStatusesErrors = (state) => state.parameters.statuses.errors;
export const selectStatusesRefetchFlags = (state) => state.parameters.statuses.refetchFlags;
export default statusesSlice.reducer;
